import React, { useState, useEffect } from "react";
import { useParams, NavLink, useSearchParams } from "react-router-dom";
import { Button, Modal, Badge, Form } from "react-bootstrap";
import { Eye, Pencil, Download, Mail, Ban, FileText } from "lucide-react";
import { toast } from "react-toastify";
import { useSession } from "../../auth/SessionProvider";
import useResource from "../../shared/useResource";
import {
  Header,
  Toolbar,
  Status,
  Pager,
  Confirm,
  date,
  money,
  Field,
} from "../../shared/UI";
import { request } from "../../api/http";
import DocumentViewer, { downloadDocument } from "../../shared/DocumentViewer";
import DocumentoEditorBase from "../../components/documentos/DocumentoEditorBase";
import {
  obtenerDocumento,
  obtenerDetallesDocumento,
  cancelarDocumento,
} from "../../api/documentos";
import { configFor } from "./documentRules";
const types = {
  cotizaciones: "CVTA",
  pedidos: "PVTA",
  remisiones: "REM",
  facturas: "FACC",
  credito: "NCV",
};
const labels = {
  cotizaciones: "Cotizaciones",
  pedidos: "Pedidos",
  remisiones: "Remisiones",
  facturas: "Facturas y notas",
  credito: "Notas de crédito",
};
export default function VentasPage() {
  const { kind } = useParams(),
    { session } = useSession();
  const [params] = useSearchParams();
  const [search, setSearch] = useState(""),
    [page, setPage] = useState(1),
    [status, setStatus] = useState("vigentes"),
    [view, setView] = useState(null),
    [editor, setEditor] = useState(null),
    [choose, setChoose] = useState(false),
    [action, setAction] = useState(null),
    [busy, setBusy] = useState(null),
    [email, setEmail] = useState("");
  const [from, setFrom] = useState(params.get("desde") || ""),
    [until, setUntil] = useState(params.get("hasta") || "");
  const type = types[kind];
  const filters = [
    {
      relation: "documentoTipo",
      property: "clave_documento_tipo",
      operator: "=",
      value: type,
    },
  ];
  if (status !== "todos")
    filters.push({
      property: "estatus",
      operator: status === "cancelados" ? "=" : "!=",
      value: "C",
    });
  if (from) filters.push({ property: "fecha", operator: ">=", value: from });
  if (until) filters.push({ property: "fecha", operator: "<=", value: until });
  if (search)
    filters.push({
      group: [
        { property: "folio", operator: "LIKE", value: search, condition: "OR" },
        {
          property: "referencia",
          operator: "LIKE",
          value: search,
          condition: "OR",
        },
        {
          relation: "cliente",
          property: "nombre",
          operator: "LIKE",
          value: search,
          condition: "OR",
        },
      ],
      condition: "AND",
    });
  const { data, loading, error, reload } = useResource(
    `encabezados?page=${page}&limit=25&filter=${encodeURIComponent(
      JSON.stringify(filters)
    )}&sort=${encodeURIComponent(
      JSON.stringify([{ property: "fecha", direction: "DESC" }])
    )}`
  );
  useEffect(() => {
    if (params.get("nuevo")) setChoose(true);
  }, [params]);
  const metadata = useResource(
    `documentos?limit=100&filter=${encodeURIComponent(
      JSON.stringify([
        {
          relation: "documentoTipo",
          property: "clave_documento_tipo",
          operator: "=",
          value: type,
        },
      ])
    )}`
  );
  const allowed = (action) =>
    session.usuario.roles.some((r) => r.name === "ADMIN") ||
    session.permissions?.includes("site.admin") ||
    session.actions.some(
      (p) =>
        p.clave_docto_tipo === type &&
        (p[action] === true || p[action] === 1 || p[action] === "1")
    );
  const edit = async (r) => {
    setBusy(r.id);
    try {
      const info = await request(`comercial-app/documentos/${r.id}/acciones`);
      if (!info.editar) throw new Error("Este documento no permite edición.");
      const [d, lines] = await Promise.all([
        obtenerDocumento(r.id),
        obtenerDetallesDocumento(r.id),
      ]);
      setEditor({
        config: configFor({ ...d.documento, documento_tipo: d.documento_tipo }),
        record: { ...d, detalles: lines.data || lines },
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  const run = async () => {
    setBusy(action.row.id);
    try {
      if (action.kind === "cancel") await cancelarDocumento(action.row.id);
      else
        await request(`comercial-app/documentos/${action.row.id}/correo`, {
          method: "POST",
          body: { email },
        });
      toast.success(
        action.kind === "cancel" ? "Documento cancelado" : "Correo enviado"
      );
      setAction(null);
      reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <Header
        title={labels[kind]}
        description="Consulta y administra tus documentos comerciales."
        action="Crear documento"
        onNew={allowed("crear") ? () => setChoose(true) : undefined}
      />
      <div className="module-tabs">
        {Object.keys(types)
          .filter((k) => session.modules[k])
          .map((k) => (
            <NavLink to={`/ventas/${k}`} key={k}>
              {labels[k]}
            </NavLink>
          ))}
      </div>
      <Toolbar
        search={search}
        onSearch={(s) => {
          setSearch(s);
          setPage(1);
        }}
        onReload={reload}
        loading={loading}
      >
        <Form.Select
          aria-label="Estado del documento"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="vigentes">Vigentes</option>
          <option value="cancelados">Cancelados</option>
          <option value="todos">Todos</option>
        </Form.Select>
        <details className="filter-details">
          <summary>Fechas</summary>
          <div className="filter-fields">
            <Field
              label="Desde"
              type="date"
              value={from}
              onChange={(v) => {
                setFrom(v);
                setPage(1);
              }}
            />
            <Field
              label="Hasta"
              type="date"
              value={until}
              onChange={(v) => {
                setUntil(v);
                setPage(1);
              }}
            />
            <Button
              variant="link"
              onClick={() => {
                setFrom("");
                setUntil("");
                setPage(1);
              }}
            >
              Limpiar
            </Button>
          </div>
        </details>
      </Toolbar>
      <Status loading={loading} error={error} onRetry={reload} />
      {!loading && !error && (
        <div className="record-list">
          {(data?.data || []).map((r) => (
            <article key={r.id} className="record">
              <div>
                <small>
                  {r.documento?.descripcion} · {r.folio}
                </small>
                <h3>{r.cliente?.nombre || "Sin cliente"}</h3>
                <p>
                  {date(r.fecha)}
                  {r.referencia ? ` · ${r.referencia}` : ""}
                </p>
              </div>
              <div className="record-facts">
                <strong>
                  {money(r.importe, r.moneda?.clave_moneda)}{" "}
                  <small>{r.moneda?.clave_moneda}</small>
                </strong>
                <Badge
                  bg={r.estatus === "C" ? "secondary" : "light"}
                  text={r.estatus === "C" ? "white" : "dark"}
                >
                  {r.estatus === "C"
                    ? "Cancelado"
                    : r.estatus === "R"
                    ? "Referenciado"
                    : "Vigente"}
                </Badge>
              </div>
              <div className="record-actions">
                <Button
                  variant="light"
                  size="sm"
                  title="Ver detalles"
                  onClick={() => setView(r.id)}
                >
                  <Eye size={17} />
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  title="Descargar PDF"
                  onClick={() =>
                    downloadDocument(r.id, "pdf").catch((e) =>
                      toast.error(e.message)
                    )
                  }
                >
                  <Download size={17} />
                </Button>
                {r.cfdi?.some((c) => c.uuid) && (
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() =>
                      downloadDocument(r.id, "xml").catch((e) =>
                        toast.error(e.message)
                      )
                    }
                  >
                    XML
                  </Button>
                )}
                {allowed("editar") &&
                  r.estatus === "N" &&
                  !r.cfdi?.some((c) => c.uuid) && (
                    <Button
                      disabled={busy === r.id}
                      variant="light"
                      size="sm"
                      title="Editar"
                      onClick={() => edit(r)}
                    >
                      <Pencil size={17} />
                    </Button>
                  )}
                <Button
                  variant="light"
                  size="sm"
                  title="Enviar por correo"
                  onClick={() => {
                    setEmail(r.cliente?.email || "");
                    setAction({ kind: "email", row: r });
                  }}
                >
                  <Mail size={17} />
                </Button>
                {allowed("cancelar") &&
                  r.estatus === "N" &&
                  !r.cfdi?.some((c) => c.uuid) && (
                    <Button
                      variant="light"
                      size="sm"
                      title="Cancelar documento"
                      onClick={() => setAction({ kind: "cancel", row: r })}
                    >
                      <Ban size={17} />
                    </Button>
                  )}
              </div>
            </article>
          ))}
          {!data?.data?.length && (
            <div className="empty">No hay documentos con estos filtros.</div>
          )}
        </div>
      )}
      <Pager data={data} page={page} setPage={setPage} />
      {choose && (
        <Modal show onHide={() => setChoose(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Selecciona el documento</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Status
              loading={metadata.loading}
              error={metadata.error}
              onRetry={metadata.reload}
            />
            <div className="document-choices">
              {(metadata.data?.data || []).map((d) => (
                <Button
                  variant="outline-primary"
                  key={d.id}
                  onClick={() => {
                    setEditor({ config: configFor(d) });
                    setChoose(false);
                  }}
                >
                  <FileText size={20} />
                  {d.descripcion}
                </Button>
              ))}
            </div>
            {!metadata.loading && !metadata.data?.data?.length && (
              <p>No hay configuraciones de documento disponibles.</p>
            )}
          </Modal.Body>
        </Modal>
      )}
      {editor && (
        <DocumentoEditorBase
          show
          config={editor.config}
          record={editor.record}
          onHide={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            reload();
          }}
        />
      )}
      {view && <DocumentViewer id={view} onClose={() => setView(null)} />}
      {action && (
        <Confirm
          title={
            action.kind === "cancel" ? "Cancelar documento" : "Enviar documento"
          }
          busy={busy === action.row.id}
          onClose={() => setAction(null)}
          onConfirm={run}
        >
          {action.kind === "cancel" ? (
            <p>¿Confirmas la cancelación del documento {action.row.folio}?</p>
          ) : (
            <Field
              label="Correo del destinatario"
              type="email"
              value={email}
              onChange={setEmail}
            />
          )}
        </Confirm>
      )}
    </>
  );
}
